import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FileText, Plus, Pencil, Eye, Loader2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { InvoiceEditModal } from '../invoices/invoice-edit-modal';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface InvoiceSidebarCardProps {
  bookingId: string;
  currency: string;
}

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  VIEWED: 'bg-purple-100 text-purple-700',
  PAID: 'bg-green-100 text-green-700',
  PARTIALLY_PAID: 'bg-yellow-100 text-yellow-700',
  OVERDUE: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-gray-100 text-gray-500',
  REFUNDED: 'bg-pink-100 text-pink-700',
};

export function InvoiceSidebarCard({ bookingId, currency }: InvoiceSidebarCardProps) {
  const { t } = useTranslation('bookings');
  const [editingInvoiceId, setEditingInvoiceId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const user = useAuthStore((state) => state.user);
  const utils = trpc.useUtils();

  const { data: invoices, isLoading } = trpc.invoice.listByBooking.useQuery(bookingId);

  const createFromBookingMutation = trpc.invoice.createFromBooking.useMutation({
    onSuccess: () => {
      utils.invoice.listByBooking.invalidate(bookingId);
      showSuccessToast(t('invoiceCard.createSuccess'));
      setIsCreating(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('invoiceCard.createError'));
      setIsCreating(false);
    },
  });

  const handleCreateInvoice = () => {
    if (!user?.id) return;
    setIsCreating(true);

    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 14);

    createFromBookingMutation.mutate({
      bookingId,
      createdById: user.id,
      invoiceType: 'INVOICE',
      dueDate,
    });
  };

  const handleEditInvoice = (invoiceId: string) => {
    setEditingInvoiceId(invoiceId);
  };

  if (isLoading) {
    return (
      <div className="border border-border bg-card p-6">
        <h2 className="font-semibold flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5" />
          {t('invoiceCard.title')}
        </h2>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('invoiceCard.title')}
          </h2>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={handleCreateInvoice}
            disabled={isCreating || createFromBookingMutation.isPending}
          >
            {isCreating || createFromBookingMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
        </div>

        {invoices && invoices.length > 0 ? (
          <div className="space-y-3">
            {invoices.map((invoice) => {
              const isOverdue =
                invoice.status !== 'PAID' &&
                invoice.status !== 'CANCELLED' &&
                invoice.status !== 'REFUNDED' &&
                new Date(invoice.dueDate) < new Date();

              return (
                <div key={invoice.id} className="border border-border p-3 space-y-2">
                  {/* Invoice Number */}
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{invoice.invoiceNumber}</span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${
                        isOverdue ? statusColors.OVERDUE : statusColors[invoice.status]
                      }`}
                    >
                      {isOverdue ? t('invoiceCard.overdue') : t(`invoiceCard.status.${invoice.status}`)}
                    </span>
                  </div>

                  {/* Type and Amount */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t(`invoiceCard.type.${invoice.invoiceType}`)}
                    </span>
                    <span className="font-medium">
                      {formatCurrency(invoice.totalAmount, currency)}
                    </span>
                  </div>

                  {/* Due Date */}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{t('invoiceCard.due', { date: new Date(invoice.dueDate).toLocaleDateString() })}</span>
                  </div>

                  {/* Balance Due if not fully paid */}
                  {invoice.balanceDue > 0 && invoice.status !== 'CANCELLED' && (
                    <div className="text-xs text-orange-600 font-medium">
                      {t('invoiceCard.balance', { amount: formatCurrency(invoice.balanceDue, currency) })}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 pt-1">
                    <Link to={`/travel/invoices/${invoice.id}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full">
                        <Eye className="mr-1 h-3 w-3" />
                        {t('invoiceCard.view')}
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEditInvoice(invoice.id)}
                    >
                      <Pencil className="mr-1 h-3 w-3" />
                      {t('invoiceCard.edit')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-muted-foreground text-sm mb-4">{t('invoiceCard.noInvoices')}</p>
            <Button
              onClick={handleCreateInvoice}
              disabled={isCreating || createFromBookingMutation.isPending}
            >
              {isCreating || createFromBookingMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              {t('invoiceCard.createInvoice')}
            </Button>
          </div>
        )}
      </div>

      {/* Invoice Edit Modal */}
      {editingInvoiceId && (
        <InvoiceEditModal
          open={!!editingInvoiceId}
          onOpenChange={(open) => !open && setEditingInvoiceId(null)}
          invoiceId={editingInvoiceId}
          onSuccess={() => utils.invoice.listByBooking.invalidate(bookingId)}
        />
      )}
    </>
  );
}
